import { useIsAuthenticated } from '@azure/msal-react';
import { Stack } from '@mui/material';
import { useStatsigClient } from '@statsig/react-bindings';
import React, { useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';

import introEloraPlatform from '../../assets/voice_demos/intro_elora_platform.mp3';

import AgentProfileAvatar from '../../components/shared/AgentProfileAvatar/AgentProfileAvatar';
import StyledCTAButton from '../../components/shared/Button/StyledCTAButton';
import Page from '../../components/shared/Page/Page';
import PrimaryFooter from '../../components/shared/PrimaryFooter/PrimaryFooter';
import Text from '../../components/shared/Text/Text';
import { appendEmbedParam } from '../../helpers/embedMode';
import { useTranslation } from '../../hooks/useTranslation';
import Loading from '../../components/shared/Loading/Loading';
import useEmbedMode from '../../hooks/useEmbedMode';

import HorizontalStack from '../../styles/HorizontalStack';
import StyledTextHighlight from '../../styles/StyledTextHighlight';

import useAuth from '../../hooks/useAuth';
import { AnalyticsEvents } from '../../enums/AnalyticsEvents';
import { useAnalytics } from '../../hooks/useAnalytics';

const Index: React.FC = () => {
  const { login } = useAuth();
  const isAuthenticated = useIsAuthenticated();
  const [searchParams] = useSearchParams();
  const [isPlaying, setIsPlaying] = useState(false);
  const { client } = useStatsigClient();
  const { track } = useAnalytics();
  const { t } = useTranslation();
  const isEmbed = useEmbedMode();

  const fromWebsite = searchParams.get('from') === 'elora.lu';

  // Auto-login if coming from elora.lu website (not in embed mode)
  useEffect(() => {
    if (fromWebsite && !isAuthenticated && !isEmbed) {
      login();
    }
  }, [fromWebsite, isAuthenticated, isEmbed, login]);

  if (isAuthenticated) {
    return <Navigate to={appendEmbedParam('/dashboard')} />;
  }

  if (fromWebsite && !isEmbed) {
    return <Loading />;
  }

  const handleStartHereClick = () => {
    track(AnalyticsEvents.HOME_PAGE_START_HERE_BUTTON_CLICKED);
    track(AnalyticsEvents.SIGN_UP_STARTED);
    login();
  };

  const handlePlayDemo = () => {
    setIsPlaying(!isPlaying);
    client.logEvent('home_page_play_button_clicked');
    track(AnalyticsEvents.HOME_PAGE_DEMO_BUTTON_CLICKED);
  };

  return (
    <Page>
      <Stack sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <HorizontalStack marginTop="80px" maxWidth="1280px">
          <Stack
            sx={{
              flex: '1',
            }}
          >
            <Stack spacing={4}>
              <Stack spacing={1}>
                <Text
                  type="h1"
                  maxWidth={600}
                  lineHeight={1.167}
                  letterSpacing="-0.01562em"
                >
                  <span style={{ display: 'block' }}>{t('Pick Up Calls')}</span>
                  <span style={{ display: 'block' }}>
                    {t('Instantly, Anytime')}
                  </span>
                </Text>
                <Text type="subtitle2">
                  <StyledTextHighlight>
                    {t(
                      'By creating Gen AI call assistants in less than an hour!'
                    )}
                  </StyledTextHighlight>
                </Text>
              </Stack>
              <Text type="body1">
                <span style={{ display: 'block' }}>
                  {t(
                    'Elora is an Al call assistant that knows perfectly your business,'
                  )}
                </span>
                <span style={{ display: 'block' }}>
                  {t(
                    'speaks multiple languages, transfers calls and books meetings'
                  )}
                </span>
                <span style={{ display: 'block' }}>
                  {t('to boost your customer satisfaction.')}
                </span>
              </Text>
              <StyledCTAButton
                variant="contained"
                color="primary"
                size="large"
                onClick={handleStartHereClick}
              >
                {t('Start here')}
              </StyledCTAButton>
            </Stack>
          </Stack>
          <AgentProfileAvatar
            aiAgentImage="ai-agent.png"
            aiAgentVoice={introEloraPlatform}
            hideAgentName
            isPlaying={isPlaying}
            playAgentVoice={handlePlayDemo}
            showAgentStatus={false}
            width="400px"
          />
        </HorizontalStack>
      </Stack>
      <PrimaryFooter />
    </Page>
  );
};

export default Index;
